import { useGrowthBook } from '@growthbook/growthbook-react';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { downloadStageContextAtom } from '@/components/DialogStage/atom';
import {
  isPremiumAsset,
  toDownloadStageAsset,
} from '@/components/DialogStage/download-asset-mapping';
import { DOWNLOAD_SIGNUP_PLAN_FEATURE } from '@/components/DialogStage/signupPlanStep';
import { useStage } from '@/components/DialogStage/useStage';
import {
  toVisitorPremiumDownloadVariant,
  VISITOR_PREMIUM_DOWNLOAD_FEATURE,
} from '@/components/DialogStage/visitorPremiumDownloadTest';
import { conversionPoints } from '@/components/Modals/context/ModalRoutingContext';
import { ModalConversionPoints } from '@/components/Modals/shared-types';
import { useReturningUser } from '@/hooks/analytics/useReturningUser';
import { ASSET_TYPE } from '@/types/asset';
import { recordConversion } from '@/utils/analytics/conversion';

import type { AssetGroup } from '@/types/asset';

/**
 * Opens the new DialogStage download journey for an anonymous user who tried
 * to download an asset (DEV-6514). Mirrors {@link useOpenAuthDialog}'s
 * returning-user routing, but seeds the download-stage context so the
 * create-account scene shows the asset they were downloading:
 *
 * - Returning-user session → the download flow's "Log in" scene (shows the
 *   asset, per the ticket's "existing user → Log in modal").
 * - New user, free asset → the "Create your free account to download" scene,
 *   or, in the DEV-7278 test's `plan-choice` arm, the plan choice before it.
 * - New user, premium asset → the premium-gate entry of the premium journey,
 *   or for Music, whichever arm of the visitor premium download test
 *   (DEV-7217) the visitor is bucketed into.
 *
 * Every path seeds the download-stage context first so the asset tile renders
 * on whichever scene opens. Callers guard on the `new-download-flow` flag and
 * their own logged-out check; this hook does not no-op for authenticated
 * users.
 */
export const useOpenDownloadAuth = () => {
  const { open } = useStage();
  const growthbook = useGrowthBook();
  const setDownloadContext = useSetAtom(downloadStageContextAtom);
  const {
    data: { isReturningUser },
  } = useReturningUser();

  return useCallback(
    (asset: AssetGroup) => {
      const isPremium = isPremiumAsset(asset);
      // Each test is evaluated only for its own population, so only they're
      // exposed: free assets for DEV-7278, premium Music for DEV-7217.
      const value =
        isPremium || isReturningUser
          ? 'control'
          : growthbook?.getFeatureValue(
              DOWNLOAD_SIGNUP_PLAN_FEATURE,
              'control'
            );
      const arm =
        value === 'plan-step' || value === 'plan-choice' ? value : undefined;
      const variant =
        isPremium && !isReturningUser && asset.type === ASSET_TYPE.MUSIC
          ? toVisitorPremiumDownloadVariant(
              growthbook?.getFeatureValue(
                VISITOR_PREMIUM_DOWNLOAD_FEATURE,
                'control'
              )
            )
          : 'control';
      setDownloadContext({
        asset: toDownloadStageAsset(asset),
        sourceAsset: asset,
        journey: isPremium ? 'premium' : 'free',
        signupPlanArm: arm,
        ...(variant === 'control' ? {} : { visitorTestVariant: variant }),
      });

      let conversionPoint = ModalConversionPoints.DownloadFlowSignUp;
      if (isReturningUser) {
        conversionPoint = ModalConversionPoints.ReturningUserLogin;
      } else if (isPremium) {
        conversionPoint = ModalConversionPoints.DownloadFlowPremiumGate;
      }
      recordConversion({
        source: conversionPoints[conversionPoint],
        assetId: Number(asset.data.id),
        assetType: asset.type,
      });

      if (isReturningUser) {
        open('download.login');
        return;
      }

      if (variant !== 'control') {
        open('download.premium-signup');
        return;
      }

      if (isPremium) {
        open('download.premium-gate');
        return;
      }
      open(arm === 'plan-choice' ? 'download.plan-choice' : 'download.signup');
    },
    [open, growthbook, setDownloadContext, isReturningUser]
  );
};
